'use client'

import { cn } from '@/lib/utils'
import React, { useLayoutEffect, useRef, useState } from 'react'

interface ExpandableTextProps {
  text: string
  className?: string
  expandText?: string
  collapseText?: string
  maxLines?: number
}

export const ExpandableText: React.FC<ExpandableTextProps> = ({
  text,
  className = '',
  expandText = 'Xem thêm',
  collapseText = 'Thu gọn',
  maxLines = 3,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const [displayText, setDisplayText] = useState(text)

  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!containerRef.current || !measureRef.current) return

    const computedStyle = getComputedStyle(containerRef.current)
    let lineHeight = parseFloat(computedStyle.lineHeight)

    if (isNaN(lineHeight)) {
      const fontSize = parseFloat(computedStyle.fontSize)
      lineHeight = fontSize * 1.5
    }

    const maxHeight = lineHeight * maxLines
    const suffix = '... ' + expandText

    measureRef.current.style.width = `${containerRef.current.clientWidth}px`

    measureRef.current.textContent = text
    const fullHeight = measureRef.current.scrollHeight

    if (fullHeight <= maxHeight || isExpanded) {
      setIsTruncated(fullHeight > maxHeight)
      setDisplayText(text)
      return
    }

    let start = 0
    let end = text.length
    let bestFit = text.length

    while (start <= end) {
      const mid = Math.floor((start + end) / 2)
      const candidate = text.slice(0, mid).trimEnd() + suffix
      measureRef.current.textContent = candidate

      if (measureRef.current.scrollHeight <= maxHeight) {
        bestFit = mid
        start = mid + 1
      } else {
        end = mid - 1
      }
    }

    setIsTruncated(true)
    setDisplayText(text.slice(0, bestFit - 8).trimEnd())
  }, [text, maxLines, isExpanded, expandText])

  return (
    <div className={cn('relative', className)}>
      <div
        ref={measureRef}
        className='invisible absolute z-[-1] whitespace-pre-wrap break-words'
        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        aria-hidden
      />
      <div
        ref={containerRef}
        className={cn('whitespace-pre-wrap break-words', className)}
      >
        {isTruncated ? (
          <>
            {isExpanded ? (
              <>
                <span dangerouslySetInnerHTML={{ __html: text }}></span>{' '}
                <button
                  onClick={() => setIsExpanded(false)}
                  className='text-primary underline underline-offset-2 hover:no-underline'
                >
                  {collapseText}
                </button>
              </>
            ) : (
              <>
                <span dangerouslySetInnerHTML={{ __html: displayText }}></span>
                {'...  '}
                <button
                  onClick={() => setIsExpanded(true)}
                  className='text-primary underline underline-offset-2 hover:no-underline'
                >
                  {expandText}
                </button>
              </>
            )}
          </>
        ) : (
          text
        )}
      </div>
    </div>
  )
}
